import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import EditableCard from 'modules/common/editableCard.component';
import React, { useState } from 'react';
import { postGlobalAssignment, updateGlobalAssignment, getGlobalInfo } from 'modules/authorizations/store/actions/global.actions';
import { POST_GLOBAL_ASSIGNMENT_FAILURE, UPDATE_GLOBAL_ASSIGNMENT_FAILURE } from 'modules/authorizations/store/types/globalTypes';
import { GET_GLOBAL_INFO_FAILURE } from 'modules/authorizations/types';

const OverviewGlobalCard = props => {
  const [isLoading, setIsLoading] = useState(false);

  const { globalInfo, id, isReadOnly, enumerations } = props;

  // Calculate assignment length in months
  const calculateAssignmentLengthInMonths = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    return months > 0 ? `${months} months` : '';
  };

  const typeOptions =
    enumerations && enumerations.globalAssignmentTypes && enumerations.globalAssignmentTypes.length > 0
      ? enumerations.globalAssignmentTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const immigrationServiceProviderOptions =
    enumerations && enumerations.immigrationServiceProviders && enumerations.immigrationServiceProviders.length > 0
      ? enumerations.immigrationServiceProviders.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const tabs = [{ header: 'Overview', order: 1, iconPath: ['fas', 'suitcase'] }];
  
  const provinces =
    enumerations && enumerations.provinces && enumerations.provinces.length > 0
      ? enumerations.provinces.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const countries =
    enumerations && enumerations.countries && enumerations.countries.length > 0
      ? enumerations.countries.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  // Sort countries alphabetically and put USA at top
  const sortedCountries = [...countries].sort((a, b) => {
    const usaVariations = ['United States of America', 'USA', 'United States', 'US'];
    const isAUsa = usaVariations.some(variation => a.title?.includes(variation));
    const isBUsa = usaVariations.some(variation => b.title?.includes(variation));
    if (isAUsa) return -1;
    if (isBUsa) return 1;
    return a.title.localeCompare(b.title);
  });

  const summaries = [
    {
      header: 'Type',
      accessorKey: 'assignmentTypeId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.assignmentTypeId,
      options: typeOptions,
      tab: 1
    },
    {
      header: 'Assignment Country',
      accessorKey: 'assignmentCountryId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.assignmentCountryId,
      options: sortedCountries,
      tab: 1
    },
    {
      header: 'Assignment City',
      accessorKey: 'assignmentCity',
      type: 'text',
      required: false,
      value: globalInfo ? globalInfo.assignmentCity : '',
      tab: 1
    },
    {
      header: 'Est Start Date',
      accessorKey: 'estimatedStart',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.estimatedStart : '',
      tab: 1
    },
    {
      header: 'Est End Date',
      accessorKey: 'estimatedEnd',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.estimatedEnd : '',
      tab: 1
    },
    {
      header: 'Assignment Length',
      accessorKey: 'assignmentLengthCalculated',
      type: 'calculated',
      required: false,
      value: globalInfo ? calculateAssignmentLengthInMonths(globalInfo.estimatedStart, globalInfo.estimatedEnd) : '',
      tab: 1,
      readOnly: true,
      calculateFn: (values) => calculateAssignmentLengthInMonths(values.estimatedStart, values.estimatedEnd)
    },
    {
      header: 'Home Country',
      accessorKey: 'homeCountryId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.homeCountryId,
      options: sortedCountries,
      tab: 1
    },
    {
      header: 'Home State',
      accessorKey: 'homeStateId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.homeStateId,
      tab: 1,
      options: provinces
    },
    {
      header: 'Citizenship',
      accessorKey: 'citizenshipId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.citizenshipId,
      options: sortedCountries,
      tab: 1
    },
    {
      header: 'Immigration Service Provider',
      accessorKey: 'immigrationServiceProviderId',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.immigrationServiceProviderId,
      options: immigrationServiceProviderOptions,
      tab: 1
    },
    {
      header: 'Notes',
      accessorKey: 'notes',
      type: 'notes',
      required: false,
      value: globalInfo?.notes,
      tab: 1
    },
    // Date fields section (consolidated from Dates card)
    {
      header: 'Actual Start',
      accessorKey: 'actualStart',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.actualStart : '',
      tab: 1
    },
    {
      header: 'Actual End',
      accessorKey: 'actualEnd',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.actualEnd : '',
      tab: 1
    },
    {
      header: 'Hire Date',
      accessorKey: 'hireDate',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.hireDate : '',
      tab: 1
    },
    {
      header: 'Completed Date',
      accessorKey: 'completedDate',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.completedDate : '',
      tab: 1
    },
    {
      header: 'Cancelled Date',
      accessorKey: 'cancelledDate',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.cancelledDate : '',
      tab: 1
    },
    {
      header: 'Return to Home',
      accessorKey: 'isReturnToHome',
      type: 'checkbox',
      required: false,
      value: globalInfo ? globalInfo?.isReturnToHome : false,
      tab: 1
    }
  ];

  const updateFields = async (tab, values) => {
    setIsLoading(true);
    if (globalInfo) {
      let newValues = { ...globalInfo };

      newValues.assignmentTypeId = values.assignmentTypeId;
      newValues.assignmentCountryId = values.assignmentCountryId;
      newValues.assignmentCity = values.assignmentCity;
      newValues.estimatedStart = values && values.estimatedStart && values.estimatedStart.length > 0 ? values.estimatedStart : null;
      newValues.estimatedEnd = values && values.estimatedEnd && values.estimatedEnd.length > 0 ? values.estimatedEnd : null;
      newValues.homeCountryId = values.homeCountryId;
      newValues.homeStateId = values.homeStateId;
      newValues.citizenshipId = values.citizenshipId;
      newValues.immigrationServiceProviderId = values.immigrationServiceProviderId;
      newValues.notes = values.notes;
      newValues.actualStart = values && values.actualStart && values.actualStart.length > 0 ? values.actualStart : null;
      newValues.actualEnd = values && values.actualEnd && values.actualEnd.length > 0 ? values.actualEnd : null;
      newValues.hireDate = values && values.hireDate && values.hireDate.length > 0 ? values.hireDate : null;
      newValues.completedDate = values && values.completedDate && values.completedDate.length > 0 ? values.completedDate : null;
      newValues.cancelledDate = values && values.cancelledDate && values.cancelledDate.length > 0 ? values.cancelledDate : null;
      newValues.isReturnToHome = values && values.isReturnToHome;

      const resp = await props.updateGlobalAssignment(newValues);
      if (resp.type === UPDATE_GLOBAL_ASSIGNMENT_FAILURE) {
        props.showToast('Failed to update global service info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setIsLoading(false);
        return false;
      } else {
        const getResp = await props.getGlobalInfo(id);
        if (getResp.type === GET_GLOBAL_INFO_FAILURE) {
          props.showToast('Failed to get global service info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setIsLoading(false);
          return false;
        }
        else {
          setIsLoading(false);
          return true;
        }
        
      }
    } else {

      let newValues = { 
        authorizationFileId: id,
        assignmentTypeId: values.assignmentTypeId,
        assignmentCountryId: values.assignmentCountryId,
        assignmentCity: values.assignmentCity,
        assignmentLengthId: '00000000-0000-0000-0000-000000000000',
        clientAssignmentNumber: '',
        homeCountryId: values.homeCountryId,
        homeStateId: values.homeStateId,
        citizenshipId: values.citizenshipId,
        immigrationServiceProviderId: values.immigrationServiceProviderId,
        payInCurrencyId: '00000000-0000-0000-0000-000000000000',
        isEligibleForTaxService: false,
        notes: values.notes,
        tripCapAmount: 0,
        maxNumberOfTrips: '00000000-0000-0000-0000-000000000000',
        estimatedStart: values.estimatedStart,
        estimatedEnd: values.estimatedEnd,
        actualStart: values.actualStart,
        actualEnd: values.actualEnd,
        hireDate: values.hireDate,
        completedDate: values.completedDate,
        cancelledDate: values.cancelledDate,
        certOfCoverageExpires: null,
        isReturnToHome: values.isReturnToHome
       };

      const resp = await props.postGlobalAssignment(newValues);
      if (resp.type === POST_GLOBAL_ASSIGNMENT_FAILURE) {
        props.showToast('Failed to update global service info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setIsLoading(false);
        return false;
      } else {
        const getResp = await props.getGlobalInfo(id);
        if (getResp.type === GET_GLOBAL_INFO_FAILURE) {
          props.showToast('Failed to get global service info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setIsLoading(false);
          return false;
        }
        else {
          setIsLoading(false);
          return true;
        }
      }
    }
  };

  return (
    <EditableCard
      tabs={tabs}
      summaries={summaries}
      numColumns={5}
      updateFields={updateFields}
      isLoading={isLoading}
      hideEdit={isReadOnly}
    />
  );
};

export default compose(
  connect(null, {
    updateGlobalAssignment,
    postGlobalAssignment,
    getGlobalInfo,
    showToast
  })
)(OverviewGlobalCard);